import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cartService from 'services/cartService';
import catalogConfigService from 'services/catalogConfigService';
import { AddCartLineResult, Cart, CartItem, CartLineInput, CartSummary } from 'types/cart';
import { ProductSummary } from 'types/product';
import { getApiErrorMessage } from 'utils/apiError';
import { useAuthContext } from 'context/AuthContext';
import { useStorefrontContext } from 'context/StorefrontContext';

interface CartContextValue {
  cart: Cart | null;
  carts: CartSummary[];
  itemCount: number;
  loading: boolean;
  refreshCart: () => Promise<void>;
  refreshCarts: () => Promise<void>;
  addProduct: (product: ProductSummary, quantity?: number) => Promise<void>;
  addItems: (lines: CartLineInput[]) => Promise<AddCartLineResult[]>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  createCart: (kind?: string, name?: string) => Promise<void>;
  activateCart: (id: number) => Promise<void>;
}

const createEmptyCart = (): Cart => ({
  items: [],
  subtotal: 0,
  discount: 0,
  total: 0,
  currency: catalogConfigService.getDefaultCurrency(),
});

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const { features } = useStorefrontContext();
  const multipleCartsEnabled = !!features.multipleCartsEnabled;
  const [cart, setCart] = useState<Cart | null>(null);
  const [carts, setCarts] = useState<CartSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshCart = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cartService.getCart();
      setCart(data);
    } catch {
      setCart(createEmptyCart());
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCarts = useCallback(async () => {
    if (!isAuthenticated || !multipleCartsEnabled) {
      setCarts([]);
      return;
    }
    try {
      const data = await cartService.listCarts();
      setCarts(data);
    } catch {
      setCarts([]);
    }
  }, [isAuthenticated, multipleCartsEnabled]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart, isAuthenticated]);

  useEffect(() => {
    refreshCarts();
  }, [refreshCarts]);

  const addProduct = async (product: ProductSummary, quantity = 1) => {
    if (product.isPaused) {
      throw new Error('Producto no disponible.');
    }
    const sku = product.sku || product.id;
    const data = await cartService.addItem({ productId: sku, quantity });
    setCart(data);
    await refreshCarts();
  };

  const addItems = useCallback(async (lines: CartLineInput[]): Promise<AddCartLineResult[]> => {
    const results: AddCartLineResult[] = [];
    let lastCart: Cart | null = null;
    for (const line of lines) {
      const sku = line.sku.trim();
      const quantity = Number(line.quantity);
      if (!sku || !Number.isFinite(quantity) || quantity <= 0) {
        results.push({ sku: line.sku, quantity: line.quantity, ok: false });
        continue;
      }
      try {
        lastCart = await cartService.addItem({ productId: sku, quantity });
        const name = lastCart.items.find((item) => item.productId.toLowerCase() === sku.toLowerCase())?.name;
        results.push({ sku, quantity, name, ok: true });
      } catch (err) {
        results.push({ sku, quantity, ok: false, error: getApiErrorMessage(err) });
      }
    }
    if (lastCart) setCart(lastCart);
    await refreshCarts();
    return results;
  }, [refreshCarts]);

  const updateQuantity = async (productId: string, quantity: number) => {
    const lineId = cart?.items.find((i) => i.productId === productId)?.lineId;
    const data = await cartService.updateItem({ productId, quantity, lineId });
    setCart(data);
    await refreshCarts();
  };

  const clearCart = async () => {
    await cartService.clearCart();
    setCart(createEmptyCart());
    await refreshCarts();
  };

  const createCart = async (kind?: string, name?: string) => {
    const data = await cartService.createCart({ kind, name });
    setCart(data);
    await refreshCarts();
  };

  const activateCart = async (id: number) => {
    const data = await cartService.activateCart(id);
    setCart(data);
    await refreshCarts();
  };

  const itemCount = useMemo(
    () => cart?.items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0) ?? 0,
    [cart]
  );

  const value = useMemo(
    () => ({
      cart,
      carts,
      itemCount,
      loading,
      refreshCart,
      refreshCarts,
      addProduct,
      addItems,
      updateQuantity,
      clearCart,
      createCart,
      activateCart,
    }),
    [cart, carts, itemCount, loading, refreshCart, refreshCarts, addItems]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCartContext debe usarse dentro de CartProvider');
  return ctx;
};
